import { useFormContext } from 'react-hook-form'
import { useState } from 'react'
import { BasicInput } from '../../../components/BasicInput'
import { BasicSelect } from '../../../components/BasicSelect'
import { Button } from '../../../components/ui/button'
import { states } from '../../../../app/utils/states'
import { cepService } from '../../../../app/services/cepService'
import { useToast } from '../../../components/ui/use-toast'
import { masks } from '../../../../app/utils/masks'
import { useStepValidation } from '../components/useStepValidation'
import { RegisterFormData } from '../components/types'

interface RegisterAddressProps {
  onNext: () => void
  onBack: () => void
}

const RegisterAddress = ({ onNext, onBack }: RegisterAddressProps) => {
  const {
    register,
    setValue,
    formState: { errors, touchedFields, isSubmitted },
  } = useFormContext<RegisterFormData>()

  const isValid = useStepValidation('address')
  const [isLoadingCep, setIsLoadingCep] = useState(false)
  const { toast } = useToast()

  const handleCepSearch = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '')

    if (cleanCep.length !== 8) return

    setIsLoadingCep(true)

    try {
      const addressData = await cepService(cleanCep)
      const options = {
        shouldValidate: true,
        shouldTouch: true,
      }

      setValue('address.street', addressData.logradouro, options)
      setValue('address.neighborhood', addressData.bairro, options)
      setValue('address.city', addressData.localidade, options)
      setValue('address.state', addressData.uf, options)
      setValue('address.zipCode', cep, options)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'CEP não encontrado',
        description: 'Verifique o CEP informado.',
      })
    } finally {
      setIsLoadingCep(false)
    }
  }

  const onSubmit = () => {
    onNext()
  }

  const getError = (field: keyof RegisterFormData['address']) => {
    return touchedFields.address?.[field] || isSubmitted
      ? (errors.address?.[field]?.message as string)
      : undefined
  }

  return (
    <div className="flex flex-col gap-4 text-black03 sm:gap-6">
      <div className="text-center sm:text-left">
        <h2 className="text-xl font-semibold sm:text-2xl">Endereço</h2>
        <p className="mt-1 text-xs text-black03 sm:text-sm">
          Informe seu endereço para continuar
        </p>
      </div>

      <form className="flex flex-col gap-4 sm:gap-6">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="w-full sm:w-32">
            <BasicInput
              label="CEP"
              {...register('address.zipCode')}
              error={getError('zipCode')}
              placeholder="00000-000"
              onInput={(e) => {
                const target = e.target as HTMLInputElement
                const value = masks.zipcode(target.value)
                target.value = value
                if (value.replace(/\D/g, '').length === 8) {
                  handleCepSearch(value)
                }
              }}
              disabled={isLoadingCep}
            />
          </div>

          <div className="hidden sm:block sm:flex-1"></div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <BasicInput
              label="Rua"
              {...register('address.street')}
              error={getError('street')}
              placeholder="Rua das Flores"
            />
          </div>

          <div className="w-full sm:w-24">
            <BasicInput
              label="Número"
              {...register('address.number')}
              error={getError('number')}
              placeholder="S/n"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BasicInput
            label="Bairro"
            {...register('address.neighborhood')}
            error={getError('neighborhood')}
            placeholder="Centro"
          />

          <BasicInput
            label="Complemento (opcional)"
            {...register('address.complement')}
            error={getError('complement')}
            placeholder="Apto, bloco, etc."
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BasicInput
            label="Cidade"
            {...register('address.city')}
            error={getError('city')}
            placeholder="São Paulo"
          />

          <BasicSelect
            label="Estado"
            {...register('address.state')}
            error={getError('state')}
            options={states}
            placeholder="Selecione o estado"
          />
        </div>

        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="order-2 h-11 flex-1 text-sm sm:order-1 sm:h-12 sm:text-base"
          >
            Voltar
          </Button>

          <Button
            type="button"
            disabled={!isValid}
            onClick={onSubmit}
            className="order-1 h-11 flex-1 bg-primary text-sm text-white hover:bg-primary/90 sm:order-2 sm:h-12 sm:text-base"
          >
            Continuar
          </Button>
        </div>
      </form>
    </div>
  )
}

export default RegisterAddress
